// The core every part of the UI shares: the state, the repaint and the change
// it detects, the one undo history's step calls, the properties panel's rebuild,
// and new object ids. main.ts and the modules split out of it (src/renderer/ui/)
// import them from here; core.ts imports none of them back. What only the app
// can do (its undo history, the 3D view's refresh, the panel's body) main.ts
// installs once, at its start (installCore). docs/claude/uiModules.md.
import type {
  Discipline,
  Drawing,
  ProductInstance,
  ProductTemplate,
  Vec2,
} from '../../shared/types';
import { createChangeTracker } from '../changeTracker';
import {
  type EditEntry,
  type EditHolder,
  editingOf,
  type EditKind,
  type EditMode,
  enterEdit as enterEditMode,
  leaveEdit as leaveEditMode,
  pickInPart,
  pickRoomCorner as pickCorner,
  pickRoomWall as pickWall,
} from '../editMode';
import type { HeaterSide } from '../geometry';
import {
  type LayerModel,
  onHiddenLayer,
  onLockedLayer,
} from '../layers';
import { pickable } from '../pickStack';
import type {
  SchedCellEdit,
  SchedTableKind,
} from '../render';
import type {
  FeatureSel,
  ViewTitleRef,
} from '../selection';
import { editableRooms } from '../roomTargets';
import type { StepEnd } from '../undoHistory';
import { Viewport } from '../viewport';

export type Tool =
  | 'arc'
  | 'array-polar'
  | 'array-rect'
  | 'backrest'
  | 'bench'
  | 'bench-end'
  | 'bench-poly'
  | 'bench-round'
  | 'bench-support'
  | 'ceiling-top'
  | 'circle'
  | 'connection'
  | 'constrain'
  | 'copy'
  | 'dimension'
  | 'door'
  | 'fillet'
  | 'floor-grille'
  | 'floor-screed'
  | 'floor-screed-slope'
  | 'floor-tiled'
  | 'floor-tiled-slope'
  | 'glass'
  | 'heater-wall'
  | 'inner-wall'
  | 'leader'
  | 'light-channel'
  | 'light-cove'
  | 'light-sky'
  | 'light-spot'
  | 'light-strip'
  | 'light-strip-up'
  | 'line'
  | 'measure'
  | 'mirror'
  | 'move'
  | 'offset'
  | 'opening'
  | 'pdf-scale'
  | 'poly-room'
  | 'polygon'
  | 'print-window'
  | 'rcp'
  | 'rect-room'
  | 'rectangle'
  | 'repeat'
  | 'revision-cloud'
  | 'rotate'
  | 'sculpt-bench'
  | 'section'
  | 'section-3d'
  | 'select'
  | 'text'
  | 'transform'
  | 'trim'
  | 'wall'
  | 'zoom';

/** A snap target: a world point, plus an anchor when it belongs to an instance. */
export type SnapKind =
  | 'center'
  | 'endpoint'
  | 'intersection'
  | 'midpoint'
  | 'nearest'
  | 'node'
  | 'perpendicular'
  | 'quadrant';

export type DragMode =
  | 'bench-edit'
  | 'ceiling-arrow'
  | 'channel-move'
  | 'conn-label'
  | 'cube-move'
  | 'dim-offset'
  | 'door'
  | 'door-resize'
  | 'draw-edit-move'
  | 'draw-edit-resize'
  | 'draw-edit-vertex'
  | 'glass'
  | 'grip'
  | 'handle'
  | 'inner-wall'
  | 'inner-wall-move'
  | 'light-tag'
  | 'marquee'
  | 'move'
  | 'move-all'
  | 'move-bg'
  | 'move-floor'
  | 'move-floor-drain'
  | 'move-node'
  | 'move-pdf'
  | 'move-table'
  | 'move-text'
  | 'move-text-leader'
  | 'none'
  | 'opening'
  | 'pan'
  | 'plane-move'
  | 'poly-room-vertex'
  | 'rcp-move'
  | 'resize'
  | 'resize-cloud'
  | 'resize-pdf'
  | 'resize-shape'
  | 'resize-table'
  | 'resize-text'
  | 'rotate'
  | 'rotate-cloud'
  | 'rotate-floor-drain'
  | 'rotate-pdf'
  | 'rotate-shape'
  | 'rotate-text'
  | 'sculpt-trestle'
  | 'section-endpoint'
  | 'section-move'
  | 'section-view-move'
  | 'section-view-rotate'
  | 'sky-fibre'
  | 'spot-move'
  | 'support-move'
  | 'support-row'
  | 'vertex'
  | 'view-title'
  | 'wall-feature-add'
  | 'wall-feature-move'
  | 'wall-span';

export type EdgeRelationType = 'parallel' | 'perpendicular' | 'equal' | 'collinear';

export type { ViewTitleRef } from '../selection';

/** One object a move drag carries (drawingObjects.ts `movableOf`): its points, the
 *  point it is placed by (on the grid), the points it snaps by, a move, a rise. */
export interface Movable { id: string; verts: () => Vec2[]; origin: () => Vec2; snaps: () => Vec2[]; move: (dx: number, dy: number) => void; raise: (dz: number) => void; }

// The drag under way: read through `state.dragMode`, changed below (*The drag under way*).
/** What is being dragged, and how far the pointer has travelled since the press
 *  (screen px along its path): what tells a click from a drag. */
const edit: EditHolder = { now: null };
const liveDrag = { mode: 'none' as DragMode, last: { x: 0, y: 0 } as Vec2, travelled: 0 };

/** The plan's view: pan and zoom (viewport.ts). */
export const vp = new Viewport();

export const state = {
  templates: [] as ProductTemplate[],
  userProducts: [] as ProductTemplate[],
  drawing: { background: null, instances: [], polygons: [], walls: [], floorings: [], dimensions: [], connections: [], units: 'mm', enteredNames: true } as Drawing,
  selectedId: null as string | null,
  // All selected instance ids (multi-select). `selectedId` is the primary (last)
  // of these; kept as a 1-element list for a single selection.
  selectedIds: [] as string[],
  selectedDimId: null as string | null,
  selectedPolyId: null as string | null,
  // All selected polygon ids (multi-select); `selectedPolyId` is the primary.
  selectedPolyIds: [] as string[],
  // A single polygon edge selected for editing (segment from vertex `edge` to `edge`+1).
  selectedPolyEdge: null as { id: string; edge: number } | null,
  // A door / pane / opening of the selected drawn wall (selection.ts).
  selectedWallFeatureId: null as string | null,
  // A constraint selected on its own: its badge, or its row (selection.ts).
  selectedConstraintId: null as string | null,
  selectedWallId: null as string | null,
  // All selected wall/line ids (multi-select); `selectedWallId` is the primary.
  selectedWallIds: [] as string[],
  selectedTextId: null as string | null,
  // All selected text-comment ids (multi-select); `selectedTextId` is the primary.
  selectedTextIds: [] as string[],
  // Which in-drawing schedule table is selected (shows resize handles).
  selectedScheduleTable: null as SchedTableKind | null,
  selectedRevCloudId: null as string | null,
  // All selected revision-cloud ids (multi-select); `selectedRevCloudId` is the primary.
  selectedRevCloudIds: [] as string[],
  // A room's feature selected — one at a time (selection.ts).
  feature: null as FeatureSel | null,
  selectedConnId: null as string | null,
  selectedSectionId: null as string | null,
  // The selected reflected ceiling plan (a view of a room, beside it).
  selectedRcpId: null as string | null,
  selectedFloorId: null as string | null,
  // Bright-green highlight of the clickable add-bench area (bench tool hover),
  // as local-frame polygons of the hovered room.
  benchAddHover: null as { instId: string; polys: Vec2[][]; kind: 'add' | 'delete' | 'pick'; shift?: Vec2 } | null,
  // Which side of the selected heater's guard cover the cursor offers: the gap's
  // outer edge on a bare side (blue, a click builds a cover) or an existing cover
  // side (red, a click clears it). Parameters only — the geometry is derived.
  heaterCoverHover: null as { instId: string; side: HeaterSide; kind: 'add' | 'remove' } | null,
  // The text comment currently being typed into (inline editor open).
  editingTextId: null as string | null,
  // The schedule-table cell currently being edited inline. Committing writes back
  // to the part name, connection label or preparation label per `edit`.
  editingCell: null as { kind: SchedTableKind; rowId: string; col: number; edit: SchedCellEdit } | null,
  /** What is being edited in place (editMode.ts): one value, changed only by
   *  `enterEdit` / `leaveEdit`. The getters below read it the old ways. */
  get editing(): EditMode | null { return edit.now; },
  /** The polygon / wall / cloud whose vertices are being edited. */
  get editVerticesId(): string | null { return editingOf(edit, 'shape')?.id ?? null; },
  /** An underlay is being moved / scaled / turned … */
  get editPdf(): boolean { return edit.now?.kind === 'underlay'; },
  /** … this one (its id). */
  get editPdfId(): string | null { return editingOf(edit, 'underlay')?.id ?? null; },
  /** The imported plan is selected to move / scale / turn (and delete). */
  get editBg(): boolean { return edit.now?.kind === 'plan'; },
  /** The room in vertex edit. */
  get polyRoomVertexEditId(): string | null { return editingOf(edit, 'room')?.id ?? null; },
  /** The corner picked in the room's vertex edit (angle / coordinate locks). */
  get polyRoomSelectedVertex(): number | null { return editingOf(edit, 'room')?.vertex ?? null; },
  /** The wall picked in the room's vertex edit (Make horizontal / vertical). */
  get polyRoomSelectedEdge(): number | null { return editingOf(edit, 'room')?.edge ?? null; },
  // Active properties-panel tab.
  propTab: 'properties' as 'properties' | 'parts' | 'constraints' | 'connections' | 'preparations' | 'drawing',
  showGrid: true,
  // The schedule overlay is visible whenever any discipline filter is on (below).
  showSchedule: true,
  showHatches: false,
  showBeams: true,
  /** The rooms' light levels over the plan (the status bar's Light levels). */
  showLux: false,
  /** Show every constraint's badge, not just the selection's. */
  showConstraints: false,
  showLabels: false,
  /** View ▸ Show ceilings: every room's ceiling lines and arrows, not only the
   *  selected room's (remembered like Hatches / Labels, off on first use). */
  showCeilings: false,
  snapEnabled: true,
  // F9: moves and grid-stepped drawing round to the grid step (gsnap).
  gridSnap: true,
  // F12: the length / angle readout at the cursor (AutoCAD's dynamic input).
  dynInput: true,
  // Settings: a finished drawing tool stays armed instead of returning to select.
  keepToolActive: false,
  // Ortho mode locks each drawn segment to horizontal/vertical; polar tracking
  // (step in degrees, 0 = off) locks it to multiples of that angle. Ortho wins
  // when both are on.
  orthoEnabled: false,
  polarStep: 0,
  // Object-snap kinds the cursor will latch onto (AutoCAD OSNAP). 'nearest' is
  // off by default (it grabs any point on an edge, which is noisy).
  osnap: { endpoint: true, midpoint: true, center: true, intersection: true, quadrant: true, perpendicular: true, nearest: false, node: true } as Record<SnapKind, boolean>,
  search: '',
  openCats: new Set<string>(),
  // Which discipline schedules are shown; each toggled independently, all on by default.
  disciplineFilters: { electrical: true, plumbing: true, mechanical: true, drainage: true, flooring: true, lighting: true } as Record<Discipline, boolean>,
  tool: 'select' as Tool,
  printA: null as Vec2 | null,
  // Transient interaction state for the dimension tool (start, end, then offset).
  pendingA: null as Vec2 | null,
  pendingB: null as Vec2 | null,
  hoverWorld: null as Vec2 | null,
  snapIndicator: null as Vec2 | null,
  snapIndicatorKind: null as SnapKind | null,
  // The vertex under the cursor that a move drag would grab (world), drawn as a
  // snap marker. When a drag begins with it set, THAT vertex is what snaps to the
  // grid and to other objects — see `grabbedVertexStart`.
  vertexGrabHover: null as Vec2 | null,
  selectedArcId: null as string | null,
  // All selected free-arc ids (multi-select); `selectedArcId` is the primary.
  selectedArcIds: [] as string[],
  selectedLeaderId: null as string | null,
  // All selected leader ids (multi-select); `selectedLeaderId` is the primary.
  selectedLeaderIds: [] as string[],
  // Active bottom tab: 'model', '3d', or a sheet id (paper space).
  viewTab: 'model' as string,
  // ---- Read and written by many of the UI's sections ----
  /** What the pointer held down is dragging (main.ts's DRAG_LIFE says what a move,
   *  the release and a cancel do). Read-only: a drag starts with `startDrag` and
   *  is over with `dropDrag` (*The drag*, docs/claude/tools.md). */
  get dragMode(): DragMode {
    return liveDrag.mode;
  },
  // Direct distance entry while drawing a polygon/wall/line vertex: the text
  // typed so far. Enter places the next vertex that far from the previous one, in
  // the current (snapped) pointing direction; Esc cancels.
  distEntry: null as string | null,
  // Unsaved-changes tracking: set on any undoable mutation, cleared on save / open
  // / new. Backs the before-unload guard so a stray close/reload doesn't lose work.
  docDirty: false,
  // A transient status message (e.g. a rejected action) that overrides the tool
  // hint for a few seconds, so the per-frame updateStatus() doesn't clobber it.
  toolStatusFlash: null as { text: string; until: number } | null,
  /** The section view a click has drilled into, or null. */
  sectionDrill: null as string | null,
  /** The selected view title — on its own, the view itself not selected. */
  selectedViewTitle: null as ViewTitleRef | null,
  // Whether the selected flooring's drain is sub-selected (a floor has one drain).
  selectedFloorDrain: false,
  // Temporary drain dimensions to each floor edge, kept during a drain drag.
  floorDrainDims: [] as Array<{ from: Vec2; to: Vec2; len: number }>,
  /** The drawing editor's picks live in its `part` mode (editMode.ts PartPick),
   *  so entering or leaving it clears them; outside it they read null and a
   *  write does nothing. Which local shape's vertex handles are shown: */
  get drawEditVerticesId(): string | null { return editingOf(edit, 'part')?.vertices ?? null; },
  set drawEditVerticesId(id: string | null) { pickInPart(edit, { vertices: id }); },
  /** The local polygon picked in the drawing editor … */
  get drawEditSelectedPolyId(): string | null { return editingOf(edit, 'part')?.poly ?? null; },
  set drawEditSelectedPolyId(id: string | null) { pickInPart(edit, { poly: id }); },
  /** … or the local line / wall. */
  get drawEditSelectedWallId(): string | null { return editingOf(edit, 'part')?.wall ?? null; },
  set drawEditSelectedWallId(id: string | null) { pickInPart(edit, { wall: id }); },
  /** An edge of the picked local polygon (Make horizontal / vertical). */
  get drawEditSelectedEdge(): { id: string; edge: number } | null { return editingOf(edit, 'part')?.edge ?? null; },
  set drawEditSelectedEdge(e: { id: string; edge: number } | null) { pickInPart(edit, { edge: e }); },
  // Extra ortho-snap vertices used at the last hover, so the guide lines can point
  // at nearby-object vertices too (not just the draft's own).
  drawSnapVerts: [] as Vec2[],
  // Alignment guide shown while dragging an existing vertex (its snapped position
  // and the vertices it lines up with), rendered like the draw-tool guides.
  editGuide: null as { at: Vec2; verts: Vec2[] } | null,
  // Thin red guide lines connecting the dragged object's centroid to another
  // object's centroid when they line up orthogonally (see centroidSnapOffset).
  centroidGuides: [] as Array<[Vec2, Vec2]>,
  // Figma-style purple alignment guides shown while moving/resizing text boxes:
  // when a dragged box's edge lines up with another box's edge, a dashed segment
  // spans between the two boxes. Filled in world (model) or paper (sheet) coords;
  // only one canvas is live at a time so a single global is enough.
  alignGuides: [] as Array<[Vec2, Vec2]>,
  // Snap targets for the shape drag in progress: every other object's vertices,
  // gathered once when the drag first asks and cleared when it ends.
  moveSnapTargets: null as Vec2[] | null,
  /** The connection points of the object under the cursor while Connect is armed. */
  connHoverDots: [] as Vec2[],
  /** The line a reflected ceiling plan is held in projection on, while placed or dragged. */
  rcpGuide: null as [Vec2, Vec2] | null,
  // PDF-scale tool: the two picked world points awaiting a real distance.
  scalePts: [] as Vec2[],
};

// ---- The drag under way (docs/claude/tools.md, *The drag*) ----
/** The press, before anything it starts: where the path is measured from. */
export function notePress(sp: Vec2): void {
  liveDrag.last = sp;
  liveDrag.travelled = 0;
}
/** Start editing this in place — whatever was being edited is left (editMode.ts). */
export function enterEdit(m: EditEntry): void { enterEditMode(edit, m); }
/** Stop editing in place — kinds given, only if the mode is one of them. */
export function leaveEdit(...kinds: EditKind[]): void { leaveEditMode(edit, ...kinds); }
/** Pick a corner in the room's vertex edit (its wall let go). */
export function pickRoomCorner(vertex: number | null): void { pickCorner(edit, vertex); }
/** Pick a wall in the room's vertex edit (its corner let go). */
export function pickRoomWall(edge: number | null): void { pickWall(edit, edge); }

/** A press starts dragging `mode`. */
export function startDrag(mode: DragMode): void {
  liveDrag.mode = mode;
}
/** The pointer moved while a drag is live. */
export function stepDrag(sp: Vec2): void {
  liveDrag.travelled += Math.hypot(sp.x - liveDrag.last.x, sp.y - liveDrag.last.y);
  liveDrag.last = sp;
}
/** Screen px the pointer has travelled since the press. */
export function dragTravelled(): number {
  return liveDrag.travelled;
}
/** The drag is over: released, put back, or handed on (an armed grip or handle). */
export function dropDrag(): void {
  liveDrag.mode = 'none';
}

// ---- What only the app can do, installed by main.ts ----
export interface CoreHooks {
  /** The one undo history (undoHistory.ts): the step calls go to it. */
  undo: () => { push(): void; begin(): unknown; end(how: StepEnd): void };
  /** What a change in the drawing sets off beyond the repaint (the 3D view's refresh). */
  changed: () => void;
  /** Rebuild the properties panel for the selection. */
  renderProperties: () => void;
  /** Refresh the status bar: the prompt, the document info. */
  updateStatus: () => void;
  /** A message on the status bar's right, for a moment (and in the command history). */
  flashStatus: (msg: string) => void;
  /** A message in place of the tool's prompt, for a moment (a refusal, a hint). */
  flashToolStatus: (text: string) => void;
  /** The drawing's layers and its one stacking order, now (layers.ts). */
  layers: () => LayerModel;
}
let hooks: CoreHooks | null = null;
/** main.ts, at its start: what the core hands on to. */
export function installCore(h: CoreHooks): void {
  hooks = h;
}
function app(): CoreHooks {
  if (!hooks) throw new Error('installCore() has not run');
  return hooks;
}

// ---- The repaint, and what changed ----
let needsRender = true;
/** What changed in the drawing (changeTracker.ts): detected, so every edit is
 *  seen however it was made; caches key on the revision, never on a repaint. */
export const changes = createChangeTracker();
/** The drawing's revision: moves only when an object in it did. */
export function drawingRev(): number {
  return changes.revision(state.drawing);
}
/** Repaint after something that may have changed the drawing (the next read of
 *  the revision looks). */
export function invalidate(): void {
  needsRender = true;
  changes.touch();
  app().changed();
}
/** Repaint after something that changes nothing in the drawing: a hover, a
 *  highlight's pulse, the view panned or zoomed. */
export function repaint(): void {
  needsRender = true;
}
/** The frame: whether a repaint was asked for since the last (and it is taken). */
export function takeRepaint(): boolean {
  const asked = needsRender;
  needsRender = false;
  return asked;
}
let backdropSig = '';
/** The backdrop canvas is drawn again on the next frame (an image it shows loaded). */
export function redrawBackdrop(): void {
  backdropSig = '';
}
/** Whether the backdrop's inputs, as `sig`, differ from the last drawn (and are now those). */
export function backdropChanged(sig: string): boolean {
  if (sig === backdropSig) return false;
  backdropSig = sig;
  return true;
}

// ---- The one undo history's step calls (docs/claude/undo.md) ----
/** Record the current drawing so the next change can be undone. Call before
 *  mutating. Inside a gesture (a focused panel field, a drag) the step starts
 *  where the GESTURE started, and takes it over: one edit, one undo. */
export function pushHistory(): void {
  app().undo().push();
}
export function beginGesture(): void {
  app().undo().begin();
}
export function commitGesture(): void {
  app().undo().end('keep');
}
/** Put the drawing back where the gesture began, recording nothing. */
export function cancelGesture(): void {
  app().undo().end('cancel');
}

/** Rebuild the properties panel for the selection (deferred while one of its
 *  fields is being typed in). */
export function renderProperties(): void {
  app().renderProperties();
}

// ---- The layers, and what they put out of reach (layers.ts, pickStack.ts) ----
/** The drawing's layers and its ONE stacking order, shared by the painter, the
 *  picker and the Parts tab (main.ts keeps it, rebuilt per drawing revision). */
export function layers(): LayerModel {
  return app().layers();
}
/** Hidden objects are not drawn, picked or snapped to. */
export function objHidden(id: string): boolean {
  return onHiddenLayer(layers(), id);
}
/** Locked objects are drawn and snapped to, but never selected or edited. */
export function objLocked(id: string): boolean {
  return onLockedLayer(layers(), id);
}
/** A room or part that takes no pick or edit: frozen, or on a hidden / locked
 *  layer (pickStack.ts `pickable`, the one rule). */
export function instLocked(inst: ProductInstance): boolean {
  return !pickable(layers(), inst.id, !!inst.frozen);
}
/** Out of reach of a click: hidden or locked (pickStack.ts `pickable`). */
export function objUnpickable(id: string): boolean {
  return !pickable(layers(), id);
}
/** The rooms a room tool may edit, top first (roomTargets.ts): not frozen, not
 *  on a hidden or locked layer. Every room tool's target is looked for in these. */
export function roomsToEdit(): ProductInstance[] {
  return editableRooms(state.drawing.instances, (r) => !instLocked(r));
}
// ---- The status bar (main.ts draws it) ----
export function updateStatus(): void {
  app().updateStatus();
}
export function flashStatus(msg: string): void {
  app().flashStatus(msg);
}
export function flashToolStatus(text: string): void {
  app().flashToolStatus(text);
}

// ---- New object ids ----
let idCounter = 0;
/** A new object id: `${prefix}-<time>-<n>`, unique in this session. */
export function newId(prefix: string): string {
  return `${prefix}-${Date.now()}-${idCounter++}`;
}
